import { useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PLATFORMS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "lucide-react";

const EMPTY_FILTERS = { q: "", min_spend: "", max_spend: "", tag: "", platform: "", category: "", state: "", purchased_after: "", purchased_before: "" };

export const SegmentBuilder = ({ categories, onSegmentsChanged }) => {
  const [segName, setSegName] = useState("");
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [previewCount, setPreviewCount] = useState(null);

  const previewSegment = async () => {
    try {
      const { data } = await api.post("/segments/preview", { filters });
      setPreviewCount(data.count);
    } catch (e) { toast.error(apiError(e)); }
  };

  const createSegment = async () => {
    try {
      await api.post("/segments", { name: segName, filters });
      toast.success(`Segment "${segName}" saved.`);
      setSegName(""); setFilters({ ...EMPTY_FILTERS }); setPreviewCount(null);
      onSegmentsChanged();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="font-serif text-xl">Create a group</CardTitle>
        <p className="text-sm text-muted-foreground">Combine any filters — for example "spent over $250" or "everyone in Texas".</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="space-y-1.5"><Label>Spent at least ($)</Label><Input type="number" value={filters.min_spend} onChange={(e) => setFilters({ ...filters, min_spend: e.target.value })} data-testid="seg-min-spend" /></div>
          <div className="space-y-1.5"><Label>Spent at most ($)</Label><Input type="number" value={filters.max_spend} onChange={(e) => setFilters({ ...filters, max_spend: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>State</Label><Input placeholder="e.g. TX" value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value })} data-testid="seg-state" /></div>
          <div className="space-y-1.5"><Label>Tag</Label><Input placeholder="e.g. Partner" value={filters.tag} onChange={(e) => setFilters({ ...filters, tag: e.target.value })} /></div>
          <div className="space-y-1.5">
            <Label>Bought category</Label>
            <Select value={filters.category || "any"} onValueChange={(v) => setFilters({ ...filters, category: v === "any" ? "" : v })}>
              <SelectTrigger data-testid="seg-category"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="any">Any category</SelectItem>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Bought from</Label>
            <Select value={filters.platform || "any"} onValueChange={(v) => setFilters({ ...filters, platform: v === "any" ? "" : v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="any">Anywhere</SelectItem>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Last purchase after</Label><Input type="date" value={filters.purchased_after} onChange={(e) => setFilters({ ...filters, purchased_after: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Last purchase before</Label><Input type="date" value={filters.purchased_before} onChange={(e) => setFilters({ ...filters, purchased_before: e.target.value })} /></div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <Button variant="secondary" onClick={previewSegment} data-testid="preview-segment-btn">See who matches</Button>
          {previewCount !== null && <p className="text-sm py-2" data-testid="segment-preview-count"><strong>{previewCount}</strong> customers match</p>}
          <div className="flex-1" />
          <div className="space-y-1.5">
            <Label>Group name</Label>
            <Input value={segName} onChange={(e) => setSegName(e.target.value)} placeholder="e.g. Texas partners" data-testid="segment-name-input" />
          </div>
          <Button onClick={createSegment} disabled={!segName.trim()} data-testid="save-segment-btn"><Plus className="h-4 w-4 mr-1" /> Save group</Button>
        </div>
      </CardContent>
    </Card>
  );
};
